import { useState, useRef, useCallback } from 'react'
import { Upload, FileText, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { personaWorkshopApi } from '@/lib/api/client'
import { useAIStore } from '@/stores/aiStore'
import type { PersonaType, PersonaWorkshopSession } from '@ispot/shared'

interface Props {
  accountId: string
  projectId: string
  open: boolean
  onClose: () => void
  onCreated: (session: PersonaWorkshopSession) => void | Promise<void>
}

export function ImportPersonaDialog({ accountId, projectId, open, onClose, onCreated }: Props) {
  const [content, setContent] = useState('')
  const [personaType, setPersonaType] = useState<PersonaType>('user')
  const [fileName, setFileName] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const { apiKey } = useAIStore()

  const handleFile = useCallback(async (file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase()
    if (!ext || !['txt', 'md', 'csv'].includes(ext)) {
      setError('Supported formats: .txt, .md, .csv')
      return
    }
    if (file.size > 500_000) {
      setError('File too large (max 500KB)')
      return
    }
    const text = await file.text()
    setContent(text)
    setFileName(file.name)
    setError(null)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file) handleFile(file)
  }, [handleFile])

  const handleSubmit = async () => {
    const trimmed = content.trim()
    if (trimmed.length < 20) {
      setError('Paste or upload at least a few sentences describing the persona.')
      return
    }
    setError(null)
    setIsProcessing(true)
    try {
      const session = await personaWorkshopApi.createFromDocument(
        accountId, projectId,
        { content: trimmed, personaType, apiKey: apiKey || undefined },
      ) as PersonaWorkshopSession
      await onCreated(session)
      resetAndClose()
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : ''
      if (msg.includes('API key') || msg.includes('401') || msg.includes('No API key')) {
        setError('No AI API key configured. Add one in Settings → AI.')
      } else {
        setError(`Failed to extract persona: ${msg || 'unknown error'}`)
      }
    } finally {
      setIsProcessing(false)
    }
  }

  const resetAndClose = () => {
    setContent('')
    setFileName(null)
    setError(null)
    setIsProcessing(false)
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) resetAndClose() }}>
      <DialogContent className="sm:max-w-[620px]" style={{ width: 'calc(100% - 2rem)', maxWidth: '620px' }}>
        <DialogHeader>
          <div className="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-market mb-1">
            Import
          </div>
          <DialogTitle className="font-display text-[20px] font-semibold tracking-tight">
            Import persona from text
          </DialogTitle>
          <DialogDescription className="mt-1 text-[13px] leading-relaxed text-text-secondary">
            Paste a persona description or upload a document. The AI extracts structured data — name, role, jobs, pains, gains.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Persona type — same chip pattern used elsewhere */}
          <div>
            <label className="mb-1.5 block text-[10.5px] font-semibold uppercase tracking-[0.12em] text-text-muted">
              Persona type
            </label>
            <div className="inline-flex items-center gap-0.5 rounded-md bg-warm-100 p-0.5">
              {(['user', 'buyer'] as const).map(t => (
                <button
                  key={t}
                  onClick={() => setPersonaType(t)}
                  className={cn(
                    'rounded px-3 py-1 text-[12.5px] font-medium transition-colors',
                    personaType === t
                      ? 'bg-bg-card text-text shadow-[0_1px_0_rgba(29,26,21,0.04)]'
                      : 'text-text-muted hover:text-text-secondary',
                  )}
                >
                  {t === 'user' ? 'User' : 'Buyer'}
                </button>
              ))}
            </div>
          </div>

          {/* Description — borderless textarea with drop zone */}
          <div>
            <div className="mb-1.5 flex items-baseline justify-between">
              <label className="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-text-muted">
                Persona description
              </label>
              <button
                onClick={() => fileRef.current?.click()}
                className="inline-flex items-center gap-1 text-[11.5px] font-medium text-text-muted transition-colors hover:text-text-secondary"
              >
                <Upload className="h-3 w-3" /> Upload .txt / .md
              </button>
            </div>
            <div
              className="relative"
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
            >
              <textarea
                value={content}
                onChange={(e) => { setContent(e.target.value); setFileName(null); setError(null) }}
                placeholder="Paste a persona description, interview notes, or user research…"
                className="h-48 w-full resize-none rounded bg-bg-secondary px-3 py-3 text-[13px] leading-relaxed text-text outline-none transition-colors hover:bg-warm-100 focus:bg-bg-card focus:shadow-[inset_2px_0_0_var(--color-market),0_0_0_1px_var(--color-warm-300)] placeholder:text-text-muted"
              />
              {!content && (
                <div className="pointer-events-none absolute inset-x-0 bottom-3 flex flex-col items-center gap-0.5 text-text-muted">
                  <Upload className="h-4 w-4 opacity-40" />
                  <span className="text-[11px] opacity-60">or drop a .txt / .md / .csv file</span>
                </div>
              )}
            </div>
            {fileName && (
              <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-text-muted">
                <FileText className="h-3 w-3" /> {fileName}
              </div>
            )}
            <input
              ref={fileRef}
              type="file"
              accept=".txt,.md,.csv"
              className="hidden"
              onChange={(e) => { if (e.target.files?.[0]) handleFile(e.target.files[0]) }}
            />
          </div>

          {error && (
            <p className="rounded bg-error/8 px-2.5 py-1.5 text-[12px] text-error">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-1 pt-2">
          <button
            onClick={resetAndClose}
            className="rounded-md px-3 py-1.5 text-[13px] font-medium text-text-secondary transition-colors hover:bg-warm-100 hover:text-text"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isProcessing || content.trim().length < 20}
            className="inline-flex items-center gap-2 rounded-md bg-market px-4 py-1.5 text-[13px] font-medium text-white transition-colors hover:bg-market-dark disabled:opacity-50"
          >
            {isProcessing ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Extracting persona…
              </>
            ) : (
              'Extract & create'
            )}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
